import React, { useEffect, useMemo, useState } from 'react';
import { BookOpen, ChevronDown, ChevronRight, Plus, Search, X } from 'lucide-react';
import { request, aiEmbedContent } from '../../utils/remote';
import usePagination from '../../school_panel/lib/usePagination';
import { fieldClass } from '../../school_panel/lib/fieldClass';
import { Badge, Button, Callout, EmptyState, ErrorPanel, LoadingState, Modal, PageHeader, PanelCard, Pager, PillTabs, Table, Td, TextLink, Th, Tabs, Track } from '../../school_panel/components/ui';
import { AppLink, LINK_ACCENT } from './adminUi';
import BookPageView from './BookPageView';

type Level = 'school' | 'university';
type IdName = { id: number; name: string };

type RagBook = {
  id: number;
  title: string;
  author?: string | null;
  is_schoolbook: boolean;
  chapters_count?: number;
  chunks_count?: number;
  faiss_index_path?: string | null;
  grade?: string | null;
  subject?: string | null;
  course_ids?: number[];
  course_names?: string[];
  fields_of_study?: IdName[];
};

type RagChapter = { id: number; chapter_number: number; title: string; start_page?: number | null; end_page?: number | null; chunks_count?: number };
type RagChunk = { id: number; text_content: string };

const PAGE_SIZE = 15;
const ALL = 'all';

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
const indexed = (b: RagBook) => !!b.faiss_index_path;
const courses = (b: RagBook): IdName[] => (b.course_ids ?? []).map((id, i) => ({ id, name: b.course_names?.[i] ?? String(id) }));
const bookCode = (b: RagBook) => String(b.subject || b.course_names?.[0] || 'BK').slice(0, 3).toUpperCase();
const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });
const num = (n: number) => n.toLocaleString();
const plural = (n: number, one: string, many = `${one}s`) => `${num(n)} ${n === 1 ? one : many}`;

// Where a book belongs: "Grade 9 · Biology", or its fields of study (courses on a second line).
const placeLine = (b: RagBook) => (b.is_schoolbook
  ? [b.grade ? `Grade ${b.grade}` : '', b.subject || ''].filter(Boolean).join(' · ') || '—'
  : (b.fields_of_study ?? []).map((f) => f.name).join(', ') || '—');

// Design `.cover`: navy book with a slate spine; a reference swaps the two.
function Cover({ code, reference, big }: { code: string; reference: boolean; big?: boolean }) {
  return (
    <span aria-hidden className={`grid flex-none place-items-center rounded-[6px] font-mono font-semibold ${big ? 'h-[68px] w-[52px] text-xs' : 'h-10 w-8 text-[10px]'} ${reference ? 'bg-slate text-mist-fixed shadow-[inset_-4px_0_0_theme(colors.navy)]' : 'bg-navy text-mist-fixed shadow-[inset_-4px_0_0_theme(colors.slate)]'}`}>
      {code}
    </span>
  );
}

// One labelled filter row (label left, pills right), as on Admin › Exams.
function FilterRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="w-full text-[13px] text-muted sm:w-28">{label}</span>
      <div className="min-w-0 max-w-full flex-1">{children}</div>
    </div>
  );
}

// Pill options with how many books each one holds, A→Z.
function countOptions(items: { key: string; label: string }[][]): { key: string; label: string; count: number }[] {
  const seen = new Map<string, { label: string; count: number }>();
  for (const list of items) {
    for (const it of list) seen.set(it.key, { label: it.label, count: (seen.get(it.key)?.count ?? 0) + 1 });
  }
  return [...seen.entries()].map(([key, v]) => ({ key, ...v })).sort((a, b) => byName(a.label, b.label));
}

// Drawer body: a book's numbers, its chapters (chunk text on demand) and a test search.
function BookDrawer({ book }: { book: RagBook }) {
  const [chapters, setChapters] = useState<RagChapter[] | null>(null);
  const [chaptersError, setChaptersError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [open, setOpen] = useState<Record<number, boolean>>({});
  // Chunk text loads only when a chapter is opened (null = that load failed).
  const [chunks, setChunks] = useState<Record<number, RagChunk[] | null>>({});
  const [fullChunk, setFullChunk] = useState<number | null>(null);
  const [vectorQuery, setVectorQuery] = useState('');
  const [vectorResults, setVectorResults] = useState<any[] | null>(null);
  const [vectorLoading, setVectorLoading] = useState(false);
  const [vectorError, setVectorError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    setChapters(null); setChaptersError(false);
    request(`/api/textbook/${book.id}/chapters/`, auth())
      .then((d: any) => { if (live) setChapters(d.chapters || []); })
      .catch(() => { if (live) setChaptersError(true); });
    return () => { live = false; };
  }, [book.id, attempt]);

  const toggleChapter = async (ch: RagChapter) => {
    const willOpen = !open[ch.id];
    setOpen((s) => ({ ...s, [ch.id]: willOpen }));
    if (!willOpen || chunks[ch.id]) return;
    try {
      const d: any = await request(`/api/textbook/chapter/${ch.id}/chunks/`, auth());
      setChunks((s) => ({ ...s, [ch.id]: d.chunks || [] }));
    } catch {
      setChunks((s) => ({ ...s, [ch.id]: null }));
    }
  };

  const runVectorSearch = async () => {
    setVectorLoading(true);
    setVectorResults(null);
    setVectorError(null);
    try {
      const token = localStorage.getItem('token');
      const embData = await aiEmbedContent({ toBeValidated: token, contents: [vectorQuery], model: 'gemini-embedding-001' });
      const embedding = embData?.embeddings?.[0]?.values;
      const vsData = await request(`/api/textbook/${book.id}/vector-search/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ embedding, top_k: 10 }),
      });
      // Higher score = better; results without a score go last.
      setVectorResults((vsData.results || []).slice().sort((a: any, b: any) => (b.score ?? Number.NEGATIVE_INFINITY) - (a.score ?? Number.NEGATIVE_INFINITY)));
    } catch (err: any) {
      setVectorError(err?.message || 'Vector search failed');
    } finally {
      setVectorLoading(false);
    }
  };

  const chunkTotal = chapters ? chapters.reduce((n, ch) => n + (ch.chunks_count ?? 0), 0) : book.chunks_count ?? 0;
  const pageCount = chapters && chapters.length > 0 ? Math.max(0, ...chapters.map((ch) => Number(ch.end_page) || 0)) : 0;
  const maxChunks = chapters ? Math.max(1, ...chapters.map((ch) => ch.chunks_count ?? 0)) : 1;

  const stat = (label: string, value: React.ReactNode) => (
    <div className="rounded-[12px] bg-surface-2 px-3 py-2.5">
      <span className="block font-mono text-lg font-semibold text-ink">{value}</span>
      <span className="text-xs text-muted">{label}</span>
    </div>
  );

  const chunkText = (tc: RagChunk) => (
    <li key={tc.id} className="text-[13px] leading-relaxed text-ink">
      {fullChunk === tc.id ? <span className="whitespace-pre-wrap">{tc.text_content}</span> : <>{tc.text_content.slice(0, 160)}{tc.text_content.length > 160 ? '…' : ''}</>}
      {tc.text_content.length > 160 && (
        <button type="button" className="ml-1.5 text-xs font-semibold text-primary hover:underline" onClick={() => setFullChunk(fullChunk === tc.id ? null : tc.id)}>
          {fullChunk === tc.id ? 'Show less' : 'Show all'}
        </button>
      )}
    </li>
  );

  return (
    <div className="space-y-5">
      <div data-rag-drawer-top tabIndex={-1} className="flex items-start gap-3.5 focus:outline-none">
        <Cover code={bookCode(book)} reference={!book.is_schoolbook} big />
        <div className="min-w-0 space-y-1">
          <p className="text-[13px] text-muted">{book.author || 'Unknown author'}</p>
          <p className="text-[13px] text-ink">{placeLine(book)}</p>
          {!book.is_schoolbook && (book.course_names ?? []).length > 0 && <p className="text-xs text-muted">{book.course_names!.join(', ')}</p>}
          <Badge tone={indexed(book) ? 'success' : 'neutral'}>{indexed(book) ? 'Indexed' : 'Not indexed'}</Badge>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {stat('Chapters', chapters ? chapters.length : book.chapters_count ?? '—')}
        {stat('Chunks', chunkTotal > 0 ? num(chunkTotal) : '—')}
        {stat('Pages', pageCount > 0 ? num(pageCount) : '—')}
      </div>

      <BookPageView book={book} />

      <section aria-label="Chapters">
        <h3 className="mb-1 text-[13px] font-semibold text-ink">Chapters</h3>
        {chaptersError ? (
          <div className="flex items-center justify-between gap-3 py-3">
            <p role="alert" className="text-sm text-danger-ink">Couldn't load chapters.</p>
            <Button variant="ghost" size="sm" onClick={() => setAttempt((n) => n + 1)}>Retry</Button>
          </div>
        ) : chapters === null ? (
          <LoadingState label="Loading chapters…" className="py-6" />
        ) : chapters.length === 0 ? (
          <p className="py-3 text-sm text-muted">No chapters found.</p>
        ) : (
          <ul>
            {chapters.map((ch) => {
              const isOpen = !!open[ch.id];
              const list = chunks[ch.id];
              const n = ch.chunks_count ?? 0;
              return (
                <li key={ch.id} className="border-b border-hairline last:border-b-0">
                  <button type="button" aria-expanded={isOpen}
                    className="flex min-h-[44px] w-full items-center gap-2.5 py-2 text-left text-[13px] focus-visible:ring-2 focus-visible:ring-accent"
                    onClick={() => toggleChapter(ch)}>
                    {isOpen ? <ChevronDown size={16} aria-hidden className="flex-none text-muted" /> : <ChevronRight size={16} aria-hidden className="flex-none text-muted" />}
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-ink">{ch.title}</span>
                      {(ch.start_page || ch.end_page) && <span className="block text-xs text-muted">Pages {ch.start_page || '–'}–{ch.end_page || '–'}</span>}
                    </span>
                    <span aria-hidden="true" className="w-12 flex-none">
                      <Track className="!h-1.5" value={(n / maxChunks) * 100} tone="slate" />
                    </span>
                    <span className="w-14 flex-none text-right font-mono text-xs text-muted" aria-label={`${n} chunks`}>{num(n)}</span>
                  </button>
                  {isOpen && (
                    list === undefined ? <LoadingState label="Loading chunks…" className="py-3" />
                      : list === null ? <p role="alert" className="mb-3 ml-7 text-xs text-danger-ink">Couldn't load chunks.</p>
                        : list.length === 0 ? <p className="mb-3 ml-7 text-xs text-muted">No chunks in this chapter.</p>
                          : <ul className="mb-3 ml-7 space-y-2.5 border-l border-line-strong pl-3">{list.map(chunkText)}</ul>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-label="Test a search" className="border-t border-hairline pt-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[13px] font-semibold text-ink">Test a search in this book</span>
          <span className="text-xs text-muted">Ask what a student might ask; the closest chunks come back with their scores.</span>
          <textarea value={vectorQuery} onChange={(e) => setVectorQuery(e.target.value)} rows={2}
            placeholder="e.g. What is photosynthesis?"
            className={`${fieldClass} min-h-[64px] py-3`} />
        </label>
        <Button variant="secondary" className="mt-2.5 w-full" onClick={runVectorSearch} disabled={vectorLoading || !vectorQuery.trim()}>
          {vectorLoading ? 'Searching…' : 'Search'}
        </Button>
        {vectorError && <p role="alert" className="mt-3 rounded-[12px] bg-danger-bg px-3 py-2 text-sm text-danger-ink">{vectorError}</p>}
        {vectorResults && (
          <div className="mt-4">
            <p className="mb-2 text-[13px] font-semibold text-ink">{plural(vectorResults.length, 'result')}</p>
            <ul className="space-y-2.5">
              {vectorResults.map((r: any, i: number) => (
                <li key={i} className="rounded-[12px] bg-surface-2 p-3">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[13px] font-semibold text-ink">Ch. {r.chapter?.chapter_number ?? '–'} · {r.chapter?.title ?? ''}</p>
                    <span className="flex-none font-mono text-xs text-muted">{typeof r.score === 'number' ? r.score.toFixed(3) : '–'}</span>
                  </div>
                  <p className="mt-1 text-[13px] text-muted">{r.chunk?.text_content?.slice(0, 240) ?? '—'}{(r.chunk?.text_content?.length ?? 0) > 240 ? '…' : ''}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

const RagList: React.FC = () => {
  // One fetch of the whole library; every filter below runs on it in the browser.
  const [books, setBooks] = useState<RagBook[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);

  const [level, setLevel] = useState<Level>('school');
  const [query, setQuery] = useState('');
  const [onlyNotIndexed, setOnlyNotIndexed] = useState(false);
  const [first, setFirst] = useState(ALL); // grade (school) or field of study (university)
  const [second, setSecond] = useState(ALL); // subject (school) or course (university)

  useEffect(() => {
    let live = true;
    setError(null);
    request('/api/textbook/list/?type=all', auth())
      .then((d: any) => { if (live) setBooks(d.books || []); })
      .catch((err: any) => { if (live) setError(err?.message || 'Unknown error'); });
    return () => { live = false; };
  }, [reloadKey]);

  const lib = books ?? [];
  const school = level === 'school';
  const levelBooks = useMemo(() => lib.filter((b) => (school ? b.is_schoolbook : !b.is_schoolbook)), [lib, school]);
  const firstKeys = (b: RagBook) => (school ? (b.grade ? [{ key: String(b.grade), label: `Grade ${b.grade}` }] : [])
    : (b.fields_of_study ?? []).map((f) => ({ key: String(f.id), label: f.name })));
  const secondKeys = (b: RagBook) => (school ? (b.subject ? [{ key: b.subject, label: b.subject }] : [])
    : courses(b).map((c) => ({ key: String(c.id), label: c.name })));

  const firstOptions = useMemo(() => countOptions(levelBooks.map(firstKeys)), [levelBooks, school]); // eslint-disable-line react-hooks/exhaustive-deps
  const inFirst = useMemo(() => levelBooks.filter((b) => first === ALL || firstKeys(b).some((k) => k.key === first)), [levelBooks, first, school]); // eslint-disable-line react-hooks/exhaustive-deps
  const secondOptions = useMemo(() => countOptions(inFirst.map(secondKeys)), [inFirst, school]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return inFirst
      .filter((b) => second === ALL || secondKeys(b).some((k) => k.key === second))
      .filter((b) => !q || [b.title, b.author, b.subject, ...(b.course_names ?? []), ...(b.fields_of_study ?? []).map((f) => f.name)]
        .some((s) => (s || '').toLowerCase().includes(q)))
      .filter((b) => !onlyNotIndexed || !indexed(b))
      .sort((a, b) => byName((a.title || '').trim(), (b.title || '').trim()));
  }, [inFirst, second, query, onlyNotIndexed, school]); // eslint-disable-line react-hooks/exhaustive-deps

  const pg = usePagination(filtered, PAGE_SIZE, [level, query, onlyNotIndexed, first, second].join('|'));
  const openBook = lib.find((b) => b.id === openId) ?? null;

  const changeLevel = (l: Level) => { setLevel(l); setFirst(ALL); setSecond(ALL); };
  const filtersOn = query.trim() !== '' || onlyNotIndexed || first !== ALL || second !== ALL;
  const clearFilters = () => { setQuery(''); setOnlyNotIndexed(false); setFirst(ALL); setSecond(ALL); };

  const libSchool = lib.filter((b) => b.is_schoolbook).length;
  const libChunks = lib.reduce((n, b) => n + (b.chunks_count ?? 0), 0);
  const notIndexed = lib.filter((b) => !indexed(b));
  const subtitle = books === null ? undefined
    : `${plural(lib.length, 'textbook')} the AI uses for notes, flashcards and chat · ${num(libChunks)} chunks · ${num(lib.length - notIndexed.length)} of ${num(lib.length)} indexed`;

  return (
    <div className="space-y-[18px]">
      <PageHeader
        eyebrow="Platform admin · Knowledge base"
        title="RAG knowledge"
        subtitle={subtitle}
        actions={(
          <AppLink to="/admin/rag-setup" className={LINK_ACCENT}>
            <Plus size={16} aria-hidden /><span>Set up new source</span>
          </AppLink>
        )}
      />

      {notIndexed.length > 0 && !onlyNotIndexed && (
        <Callout tone="warm">
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span>{plural(notIndexed.length, 'book isn’t', 'books aren’t')} indexed yet, so the AI can't search {notIndexed.length === 1 ? 'it' : 'them'}.</span>
            <button type="button" className="font-semibold underline" onClick={() => {
              const uni = notIndexed.every((b) => !b.is_schoolbook);
              changeLevel(uni ? 'university' : 'school');
              setOnlyNotIndexed(true);
            }}>Show {notIndexed.length === 1 ? 'it' : 'them'}</button>
          </span>
        </Callout>
      )}

      <PanelCard>
        <Tabs<Level>
          label="Book level"
          value={level}
          onChange={changeLevel}
          items={[
            { key: 'school', label: 'High school', count: books ? libSchool : undefined },
            { key: 'university', label: 'University', count: books ? lib.length - libSchool : undefined },
          ]}
        />

        <div className="mb-4 flex flex-col gap-3">
          <label className="relative block max-w-[520px]">
            <span className="sr-only">Search books</span>
            <Search size={16} aria-hidden className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-muted" />
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={school ? 'Search by title, author or subject' : 'Search by title, author, field of study or course'}
              className={`${fieldClass} pl-10`} />
          </label>
          {firstOptions.length > 0 && (
            <FilterRow label={school ? 'Grade' : 'Field of study'}>
              <PillTabs scroll dense label={school ? 'Grade' : 'Field of study'} value={first} onChange={(k) => { setFirst(k); setSecond(ALL); }}
                options={[{ key: ALL, label: 'All' }, ...firstOptions]} />
            </FilterRow>
          )}
          {/* The second filter appears once the first narrows the list. */}
          {first !== ALL && secondOptions.length > 1 && (
            <FilterRow label={school ? 'Subject' : 'Course'}>
              <PillTabs scroll dense label={school ? 'Subject' : 'Course'} value={second} onChange={setSecond}
                options={[{ key: ALL, label: 'All' }, ...secondOptions]} />
            </FilterRow>
          )}
          {onlyNotIndexed && (
            <div>
              <button type="button" onClick={() => setOnlyNotIndexed(false)}
                className="inline-flex items-center gap-1.5 rounded-full bg-accent-bg px-3 py-1.5 text-[13px] font-semibold text-warm-ink focus-visible:ring-2 focus-visible:ring-accent">
                Not indexed only <X size={14} aria-hidden /><span className="sr-only">(remove)</span>
              </button>
            </div>
          )}
        </div>

        {error ? (
          <ErrorPanel title="Failed to load" body={error} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : books === null ? (
          <LoadingState label="Loading books…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon={<BookOpen size={20} />} title="No books here"
            body={filtersOn
              ? <>Nothing matches these filters. <button type="button" className="font-semibold text-primary underline" onClick={clearFilters}>Clear filters</button></>
              : <>No {school ? 'high school' : 'university'} books yet. <TextLink to="/admin/rag-setup">Set up a new source</TextLink>.</>} />
        ) : (
          <>
            <Table minWidth={560}>
              <thead>
                <tr>
                  <Th>Book</Th>
                  <Th>{school ? 'Grade · subject' : 'Field of study · courses'}</Th>
                  <Th align="right">Content</Th>
                </tr>
              </thead>
              <tbody>
                {pg.pageItems.map((b) => (
                  <tr key={b.id}>
                    <Td>
                      <button type="button" onClick={() => setOpenId(b.id)} aria-label={`Open ${b.title.trim()}`}
                        className="group flex w-full min-w-0 items-center justify-start gap-3 rounded-[10px] text-left focus-visible:ring-2 focus-visible:ring-accent">
                        <Cover code={bookCode(b)} reference={!b.is_schoolbook} />
                        <span className="min-w-0">
                          <span className="block font-semibold text-ink underline-offset-[3px] group-hover:underline">{b.title}</span>
                          {b.author && <span className="block text-xs text-muted">{b.author}</span>}
                        </span>
                      </button>
                    </Td>
                    <Td>
                      <span className="block text-ink">{placeLine(b)}</span>
                      {!b.is_schoolbook && (b.course_names ?? []).length > 0 && <span className="block text-xs text-muted">{b.course_names!.join(', ')}</span>}
                    </Td>
                    <Td align="right">
                      <span className="block whitespace-nowrap text-[13px] text-muted">{plural(b.chapters_count ?? 0, 'chapter')} · {plural(b.chunks_count ?? 0, 'chunk')}</span>
                      {!indexed(b) && <span className="mt-1 inline-block"><Badge tone="neutral">Not indexed</Badge></span>}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="mt-3.5 flex flex-wrap items-center justify-between gap-2.5 border-t border-hairline pt-3.5">
              <span className="text-[13px] text-muted">
                {plural(filtered.length, 'book')}
                {filtersOn && <> · <button type="button" className="font-semibold text-primary underline" onClick={clearFilters}>Clear filters</button></>}
              </span>
              <Pager page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} label="Books pagination" />
            </div>
          </>
        )}
      </PanelCard>

      <Modal isOpen={openBook !== null} onClose={() => setOpenId(null)} side="right" size="lg"
        // Start at the top of the drawer, not on the search box below the (still loading) chapters.
        initialFocus={() => document.querySelector<HTMLElement>('[data-rag-drawer-top]')}
        eyebrow={openBook ? (openBook.is_schoolbook ? 'High school book' : 'University book') : undefined}
        title={openBook?.title.trim()}>
        {openBook && <BookDrawer key={openBook.id} book={openBook} />}
      </Modal>
    </div>
  );
};

export default RagList;
